/* ============================================================
   TypeScript types mirroring backend DTOs
   ============================================================ */

export type FileFormat = 'Stl' | 'Obj' | 'ThreeMf' | 'Step';
export type FileStatus = 'Uploaded' | 'Analyzing' | 'Analyzed' | 'Failed' | 0 | 1 | 2 | 3;
export type MaterialType =
  | 'Pla' | 'Petg' | 'Abs' | 'Tpu'
  | 'ResinStandard' | 'ResinEngineering' | 'Nylon';

export interface Material {
  id: string;
  name: string;
  type: MaterialType;
  pricePerGramRub: number;
  color: string;
  densityGperCm3?: number;
  isActive: boolean;
}

export interface ModelFileResponse {
  id: string;
  originalFileName: string;
  format: FileFormat;
  sizeBytes: number;
  /** ME-2: rendered model preview PNG (/media/model-previews/…) — null for STEP or a failed render. */
  previewUrl?: string | null;
  status: FileStatus;
  boundingBoxX: number;
  boundingBoxY: number;
  boundingBoxZ: number;
  volumeMm3: number;
  surfaceAreaMm2: number;
  isManifold: boolean;
  analysisWarnings: string | null;
  createdAtUtc: string;
}

export interface QuoteRequest {
  modelFileId: string;
  materialId?: string;
  layerHeightMm?: number;
  infillPercent?: number;
  printerId?: string;
  /* TASK-179: seller pricing path */
  providerId?: string;
  copies?: number;
  isRush?: boolean;
  slicerGrams?: number | null;
  slicerMinutes?: number | null;
  /* 00a0da87: customer post-processing toggles — each performer prices
   * them by their own rates in the match list (repeat orders carry them). */
  includePaint?: boolean;
  includeSupportRemoval?: boolean;
}

export interface QuoteResponse {
  quoteId: string;
  setupFee: number;
  materialCost: number;
  machineCost: number;
  postProcessingCost: number;
  platformFee: number;
  totalPrice: number;
  estimatedPrintMinutes: number;
  estimatedMaterialGrams: number;
  /* TASK-179 additions (absent on legacy v1 responses) */
  discountPercent?: number;
  copies?: number;
  isRush?: boolean;
  providerId?: string | null;
  slicerDataUsed?: boolean;
  /* 00a0da87: customer post-processing toggle snapshot (pricing per performer). */
  includePaint?: boolean;
  includeSupportRemoval?: boolean;
}

export interface ApiResult<T> {
  isSuccess: boolean;
  value?: T;
  error?: string;
}

/* ── TASK-197: performer matches for a completed quote ───── */

export type PrinterTechnology = 'Fdm' | 'Sla' | 'Sls' | 0 | 1 | 2;

export interface PerformerMatch {
  providerId: string;
  providerName: string;
  city: string;
  sameCity: boolean;
  isVerified: boolean;
  rating: number | null;
  reviewCount: number;
  /** TASK-247: completed orders — trust signal for quote selection. */
  completedOrderCount?: number;
  printerId: string;
  printerName: string;
  printerModel: string;
  technology: PrinterTechnology;
  printPriceRub: number;
  deliveryCostRub: number;
  totalRub: number;
  /** G8-D4: delivery priced by a live carrier tariff — UI labels it «примерно». */
  deliveryIsLiveTariff?: boolean;
  /** Carrier name behind a live tariff («СДЭК», «Почта России»). */
  deliveryCarrier?: string | null;
  /** MS-1: the performer tracks stock rows for the quote's material. */
  tracksMaterialStock?: boolean;
  /** MS-1: in-stock colors of the quote's material (remaining > 0), most first;
   *  null/undefined = not tracking (default palette), [] = tracking but sold out. */
  availableColors?: string[] | null;
  /** ecfe2257: availableColors with the performer's hex swatch per color (same order). */
  availableSwatches?: { color: string; hexColor?: string | null }[] | null;
  /** 00a0da87: what the performer offers + the customer's toggle outcome
   *  (null when the performer offers nothing and nothing was toggled). */
  postProcessing?: MatchPostProcessing | null;
  /** 2f9c70ba: the performer is the customer's active referrer — pinned
   *  first with a «Рекомендуем» badge; free choice preserved. */
  isReferred?: boolean;
}

/* ── 00a0da87: post-processing options in the match view ──── */

/** One performer-defined priced option («Шлифовка», «Пропитка»…). */
export interface MatchOption {
  name: string;
  pricePerCopyRub: number;
}

/** Post-processing view for one match (mirrors MatchPostProcessingDto). */
export interface MatchPostProcessing {
  /** Customer toggled paint on this quote. */
  includePaint: boolean;
  /** Paint charged by the estimate — printPriceRub already includes it. */
  paintApplied: boolean;
  /** False → paint price «уточняется в чате», never a fake exact number. */
  paintPriceKnown: boolean;
  /** Performer's paint rate per cm² (0 when not offered). */
  paintPricePerCm2Rub: number;
  includeSupportRemoval: boolean;
  /** False → removal price «уточняется в чате». */
  supportRemovalPriced: boolean;
  /** Performer's support-removal rate per gram (0 when not offered). */
  supportRemovalPricePerGramRub: number;
  /** 7f5734fc: support mass used for pricing, grams (0 when unknown). */
  supportGramsUsed?: number;
  /** 7f5734fc: true → the mass is an analyzer estimate, UI labels «оценка». */
  supportGramsIsEstimate?: boolean;
  /** Performer-defined extra priced options. */
  options?: MatchOption[] | null;
}

export interface PerformerMatches {
  quoteId: string;
  customerCity: string;
  matches: PerformerMatch[];
}

/* ── Quality presets (from pricing-config.json) ───────────── */

export interface QualityPreset {
  key: string;
  label: string;
  layerHeightMm: number;
  infillPercent: number;
  description: string;
}

export const QUALITY_PRESETS: QualityPreset[] = [
  {
    key: 'draft',
    label: 'Черновое',
    layerHeightMm: 0.30,
    infillPercent: 15,
    description: 'Быстро и дёшево — для прототипов и проверки посадки',
  },
  {
    key: 'standard',
    label: 'Стандарт',
    layerHeightMm: 0.20,
    infillPercent: 20,
    description: 'Баланс качества и цены — для большинства деталей',
  },
  {
    key: 'high',
    label: 'Высокое',
    layerHeightMm: 0.12,
    infillPercent: 30,
    description: 'Максимальная детализация — для презентационных образцов',
  },
];

/* ── MW-3 (40064910): MakerWorld link → prefill (docs/makerworld-link-quote-spec.md §5) ── */

/**
 * Backend serializes enums as numbers (same TASK-196 note as FileStatus);
 * string spellings accepted defensively in case a converter gets enabled.
 */
export type PrefillConfidenceDto = 0 | 1 | 2 | 3 | 'None' | 'Low' | 'Medium' | 'High';

export interface MakerWorldProfileDto {
  id: number;
  name: string | null;
  weightGrams: number | null;
  predictionSeconds: number | null;
}

export interface MakerWorldMaterialHintDto {
  filamentType: string;
  catalogType: MaterialType | number | null;
  colorHex: string | null;
  usedGrams: number | null;
}

export interface PrefillFieldDto {
  value: number | null;
  confidence: PrefillConfidenceDto;
}

export interface MakerWorldPrefillDto {
  modelId: number;
  title: string | null;
  coverUrl: string | null;
  creatorName: string | null;
  license: string | null;
  profiles: MakerWorldProfileDto[];
  selectedProfileId: number | null;
  grams: PrefillFieldDto;
  minutes: PrefillFieldDto;
  layerHeightMm: PrefillFieldDto;
  materialConfidence: PrefillConfidenceDto;
  dimensionConfidence: PrefillConfidenceDto;
  materialHints: MakerWorldMaterialHintDto[];
}

export interface MakerWorldPrefillResultDto {
  found: boolean;
  reason: string | null;
  prefill: MakerWorldPrefillDto | null;
}

/* ============================================================
   33f30c97: order-scoped customer↔performer chat
   ============================================================ */

export interface OrderChatMessage {
  messageId: string;
  orderId: string;
  senderUserId: string;
  isFromProvider: boolean;
  body: string;
  /** fa58b0eb: attachment URLs (≤3, /media/order-chat/…) — empty for text-only. */
  photoUrls?: string[];
  sentAtUtc: string;
}

export interface OrderChatThread {
  /** Chronological page (oldest → newest) of the latest messages. */
  messages: OrderChatMessage[];
  hasMore: boolean;
  unreadCount: number;
  canPost: boolean;
  lockReason: string | null;
}
